import React from 'react';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import axios from 'axios';
import Typography from '@material-ui/core/Typography';
import EnhancedTableHead from './EnhancedTableHead';
import toRegionAbbrev from '../../utils/toRegionAbbrev';

function getSorting(order, orderBy) {
	if (
		orderBy === 'name' ||
		orderBy === 'address' ||
		orderBy === 'city' ||
		orderBy === 'province'
	) {
		return order === 'desc'
			? (a, b) => String(a[orderBy]).localeCompare(String(b[orderBy]))
			: (a, b) => String(b[orderBy]).localeCompare(String(a[orderBy]));
	}

	return order === 'desc'
		? (a, b) => b[orderBy] - a[orderBy]
		: (a, b) => a[orderBy] - b[orderBy];
}

const styles = (theme) => ({
	root: {
		width: '100%',
		marginTop: theme.spacing.unit * 3,
		marginBottom: theme.spacing.unit * 3,
		borderRadius: 12,
		boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
		overflow: 'hidden',
		backgroundColor: '#fff',
	},
	header: {
		backgroundColor: '#f8fafc',
		padding: '24px 24px 20px',
		borderBottom: '1px solid #f3f4f6',
		display: 'flex',
		flexDirection: 'column',
	},
	headerTitle: {
		fontWeight: 400,
		fontSize: '14px',
		color: '#22223b',
		marginBottom: 4,
	},
	headerSubtitle: {
		fontWeight: 400,
		fontSize: '0.875rem',
		color: '#757575',
		marginTop: '-2px',
	},
	table: {
		minWidth: 1020,
		'& thead th': {
			fontWeight: 600,
			fontSize: '0.75rem',
			color: '#64748b',
			backgroundColor: '#f8fafc',
			padding: '12px 8px',
			borderBottom: '2px solid #f3f4f6',
			textTransform: 'uppercase',
			letterSpacing: '0.03em',
			lineHeight: 1.2,
			whiteSpace: 'nowrap',
		},
		'& tbody td, & tbody th': {
			padding: '12px 8px',
			fontSize: '0.875rem',
			color: '#475569',
			borderBottom: '1px solid #f1f5f9',
			verticalAlign: 'middle',
		},
	},
	tableWrapper: {
		overflowX: 'auto',
	},
	button: {
		marginLeft: theme.spacing.unit * 2,
		marginTop: theme.spacing.unit * 1.5,
	},
	input: {
		display: 'none',
	},
	addButton: {
		backgroundColor: '#c655d6',
		color: '#fff',
		minWidth: '120px',
		textTransform: 'none',
		fontWeight: 500,
		padding: '8px 20px',
		borderRadius: '8px',
		boxShadow: 'none',
		'&:hover': {
			backgroundColor: '#b94bc8',
			boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
		},
	},
	addButtonWrapper: {
		padding: '16px 0 16px 24px',
	},
});

class EnhancedTable extends React.Component {
	getProvinceHeaderLabel = (locations) => {
		const CANADA = new Set([
			'AB',
			'BC',
			'MB',
			'NB',
			'NL',
			'NS',
			'NT',
			'NU',
			'ON',
			'PE',
			'QC',
			'SK',
			'YT',
		]);
		const US = new Set([
			'AL',
			'AK',
			'AZ',
			'AR',
			'CA',
			'CO',
			'CT',
			'DE',
			'FL',
			'GA',
			'HI',
			'ID',
			'IL',
			'IN',
			'IA',
			'KS',
			'KY',
			'LA',
			'ME',
			'MD',
			'MA',
			'MI',
			'MN',
			'MS',
			'MO',
			'MT',
			'NE',
			'NV',
			'NH',
			'NJ',
			'NM',
			'NY',
			'NC',
			'ND',
			'OH',
			'OK',
			'OR',
			'PA',
			'RI',
			'SC',
			'SD',
			'TN',
			'TX',
			'UT',
			'VT',
			'VA',
			'WA',
			'WV',
			'WI',
			'WY',
			'DC',
		]);

		const codes = (Array.isArray(locations) ? locations : [])
			.map((l) => (l && l.province ? String(l.province).trim() : ''))
			.filter(Boolean)
			.map((p) => p.toUpperCase());

		if (!codes.length) return 'State/Province';

		const hasUSOnly = codes.some((c) => US.has(c) && !CANADA.has(c));
		const hasCanadaOnly = codes.some((c) => CANADA.has(c) && !US.has(c));

		if (hasUSOnly && !hasCanadaOnly) return 'State';
		if (hasCanadaOnly && !hasUSOnly) return 'Province';
		return 'State/Province';
	};

	constructor(props) {
		super(props);

		this.state = {
			order: 'asc',
			orderBy: 'address',
			selected: [],
			data: [],
			page: 0,
			rowsPerPage: 25,
			loading: true,
			allSelected: false,
		};
	}

	async componentDidMount() {
		const res = await axios.get('/api/locations');
		const data = (res.data && res.data.data) || [];
		const alreadySelectedCount =
			(res.data && res.data.alreadySelectedCount) || '0';
		const numberOfLocations =
			(res.data && res.data.numberOfLocations) || '0';
		this.setState({
			data,
			alreadySelectedCount,
			numberOfLocations,
			loading: false,
			selected: [],
		});
	}

	getTitle = () =>
		this.state.alreadySelectedCount && this.state.numberOfLocations
			? `You have added ${this.state.alreadySelectedCount} of ${this.state.numberOfLocations} locations.`
			: `Select locations to add to Chekkit.`;

	confirmLocations = async () => {
		const { selected } = this.state;
		this.setState({ loading: true });

		// TODO: post selected to api
		const post = await axios.post('/api/locations/', {
			googleLocationPaths: selected,
		});

		if (post.error) {
			alert(
				'There was a problem selecting your location. Please try again.'
			);
		}

		const res = await axios.get('/api/locations');
		const { data, alreadySelectedCount } = res.data || [];
		this.setState({
			data,
			alreadySelectedCount,
			loading: false,
			selected: [],
		});
	};

	handleSelectAllClick = (event) => {
		const { allSelected } = this.state;
		if (!allSelected) {
			this.setState((state) => ({
				selected: state.data.map((n) => n.id),
				allSelected: !allSelected,
			}));
			return;
		}
		this.setState({ selected: [], allSelected: !allSelected });
	};

	handleRequestSort = (event, property) => {
		const orderBy = property;
		let order = 'desc';

		if (this.state.orderBy === property && this.state.order === 'desc') {
			order = 'asc';
		}

		this.setState({ order, orderBy });
	};

	handleClick = (event, id) => {
		const { selected } = this.state;
		const selectedIndex = selected.indexOf(id);
		let newSelected = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selected, id);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selected.slice(1));
		} else if (selectedIndex === selected.length - 1) {
			newSelected = newSelected.concat(selected.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selected.slice(0, selectedIndex),
				selected.slice(selectedIndex + 1)
			);
		}

		this.setState({ selected: newSelected });
	};

	handleChangePage = (event, page) => {
		this.setState({ page });
	};

	handleChangeRowsPerPage = (event) => {
		this.setState({ rowsPerPage: event.target.value });
	};

	isSelected = (id) => this.state.selected.indexOf(id) !== -1;

	renderSkeletonRow = (rowIdx) => {
		const skeletonStyle = {
			background:
				'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%)',
			borderRadius: 4,
			minHeight: 24,
			height: 24,
			animation: 'skeleton-loading 1.2s infinite',
			backgroundSize: '400% 100%',
			margin: 0,
		};
		// Using rowIdx as key is acceptable for loading skeletons
		// eslint-disable-next-line react/no-array-index-key
		return (
			<TableRow key={`skeleton-${rowIdx}`}>
				{[...Array(5)].map((_, colIdx) => (
					// eslint-disable-next-line react/no-array-index-key
					<TableCell key={`skeleton-cell-${rowIdx}-${colIdx}`}>
						<div style={skeletonStyle} />
					</TableCell>
				))}
			</TableRow>
		);
	};

	renderLocationTable = () => {
		const { classes } = this.props;
		const {
			data = [],
			order,
			orderBy,
			selected,
			rowsPerPage,
			page,
			loading,
			allSelected,
		} = this.state;
		const provinceLabel = this.getProvinceHeaderLabel(data);
		const emptyRows = 0;

		return (
			<div>
				<Paper className={classes.root}>
					<div className={classes.header}>
						<Typography
							variant='h6'
							className={classes.headerTitle}
						>
							Add Locations
						</Typography>
						<Typography
							className={`${classes.headerSubtitle} subtitle`}
						>
							{this.getTitle()}
						</Typography>
					</div>
					<div className={classes.tableWrapper}>
						<Table
							className={classes.table}
							aria-labelledby='tableTitle'
						>
							<EnhancedTableHead
								numSelected={selected.length}
								order={order}
								orderBy={orderBy}
								onRequestSort={this.handleRequestSort}
								onSelectAllClick={this.handleSelectAllClick}
								rowCount={data.length}
								allSelected={allSelected}
								provinceLabel={provinceLabel}
							/>
							<TableBody>
								{loading
									? Array.from({ length: 10 }).map((_, idx) =>
											this.renderSkeletonRow(idx)
									  )
									: data
											.sort(getSorting(order, orderBy))
											.slice(
												page * rowsPerPage,
												page * rowsPerPage + rowsPerPage
											)
											.map((n) => {
												const isSelected =
													this.isSelected(n.id);
												return (
													<TableRow
														hover
														onClick={(event) =>
															this.handleClick(
																event,
																n.id
															)
														}
														role='checkbox'
														aria-checked={
															isSelected
														}
														tabIndex={-1}
														key={n.id}
														selected={isSelected}
													>
														<TableCell padding='checkbox'>
															<Checkbox
																checked={
																	isSelected
																}
															/>
														</TableCell>
														<TableCell
															component='th'
															scope='row'
															padding='none'
														>
															{n.name}
														</TableCell>
														<TableCell>
															{n.address}
														</TableCell>
														<TableCell>
															{n.city}
														</TableCell>
														<TableCell>
															{toRegionAbbrev(n.province)}
														</TableCell>
													</TableRow>
												);
											})}
								{emptyRows > 0 && (
									<TableRow
										style={{ height: 49 * emptyRows }}
									>
										<TableCell colSpan={6} />
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
					<div>
						<Grid container spacing={24}>
							<Grid item xs={5}>
								<div className={classes.addButtonWrapper}>
									<Button
										size='large'
										variant='contained'
										color='secondary'
										className={classes.addButton}
										onClick={this.confirmLocations}
									>
										Add Locations
									</Button>
								</div>
							</Grid>
							<Grid item xs={2} />

							<Grid item xs>
								<TablePagination
									component='div'
									count={data.length}
									rowsPerPage={rowsPerPage}
									page={page}
									backIconButtonProps={{
										'aria-label': 'Previous Page',
									}}
									nextIconButtonProps={{
										'aria-label': 'Next Page',
									}}
									onChangePage={this.handleChangePage}
									onChangeRowsPerPage={
										this.handleChangeRowsPerPage
									}
								/>
							</Grid>
						</Grid>
					</div>
				</Paper>
			</div>
		);
	};

	render() {
		return <div>{this.renderLocationTable()}</div>;
	}
}

export default withStyles(styles)(EnhancedTable);



// WEBPACK FOOTER //
// ./src/components/add_accounts/SelectLocationsTable.js